import React, { useEffect, useState } from "react";
import { Package, UploadCloud, Loader2, Eye, EyeOff } from "lucide-react";
import { http, errMsg } from "@/lib/api";
import { toast } from "sonner";
import { SACard } from "@/components/superadmin/SAPrimitives";

const PLATFORMS = [
    { v: "windows", label: "Windows" },
    { v: "macos", label: "macOS" },
    { v: "linux", label: "Linux" },
];
const CHANNELS = ["stable", "preview", "beta"];

/**
 * SuperAdminDesktopReleases — publish/unpublish MoovBytes Desktop
 * installer builds. Separate page from SuperAdminReleases.jsx (Agent
 * fleet rollout) on purpose: different product surface, different
 * backend router (routers/desktop_releases.py vs. routers/agent_updates.py),
 * and a materially simpler workflow (no canary/rollout metrics, just
 * publish/unpublish of one file per platform+version+channel).
 */
export default function SuperAdminDesktopReleases() {
    const [releases, setReleases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState("");
    const [busyId, setBusyId] = useState(null);

    const [form, setForm] = useState({ version: "", platform: "windows", channel: "stable", notes: "", publish: true });
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await http.get("/desktop-releases/admin");
            setReleases(data.releases || []);
            setErr("");
        } catch (e) {
            setErr(errMsg(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const upload = async (e) => {
        e.preventDefault();
        if (!file) { toast.error("Choose an installer file first."); return; }
        setUploading(true);
        try {
            const body = new FormData();
            body.append("version", form.version);
            body.append("platform", form.platform);
            body.append("channel", form.channel);
            body.append("notes", form.notes);
            body.append("publish", String(form.publish));
            body.append("installer", file);
            await http.post("/desktop-releases", body, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`v${form.version} (${form.platform}) uploaded.`);
            setForm({ ...form, version: "", notes: "" });
            setFile(null);
            load();
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setUploading(false);
        }
    };

    const togglePublished = async (release) => {
        setBusyId(release.id);
        try {
            const body = new FormData();
            body.append("published", String(!release.published));
            await http.patch(`/desktop-releases/${release.id}`, body, { headers: { "Content-Type": "multipart/form-data" } });
            toast.success(release.published ? "Unpublished." : "Published.");
            load();
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setBusyId(null);
        }
    };

    return (
        <div className="space-y-5" data-testid="sa-desktop-releases-page">
            <div>
                <h1 className="text-[22px] font-semibold tracking-tight">Desktop Releases</h1>
                <div className="mt-1 text-[12px] text-white/55">
                    Publish MoovBytes Desktop installers. SHA-256 is computed server-side on upload, never trusted from the client.
                </div>
            </div>

            <SACard>
                <div className="mb-3 flex items-center gap-2 text-[12px] font-semibold text-white/80">
                    <UploadCloud size={14} /> Publish a build
                </div>
                <form onSubmit={upload} className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
                    <Field label="Version">
                        <input
                            required
                            value={form.version}
                            onChange={(e) => setForm({ ...form, version: e.target.value })}
                            placeholder="1.2.0"
                            data-testid="sa-desktop-release-version"
                            className="w-full rounded border border-white/10 bg-white/[0.03] px-2.5 py-1.5 text-[12px] text-white/90"
                        />
                    </Field>
                    <Field label="Platform">
                        <select
                            value={form.platform}
                            onChange={(e) => setForm({ ...form, platform: e.target.value })}
                            data-testid="sa-desktop-release-platform"
                            className="w-full rounded border border-white/10 bg-white/[0.03] px-2.5 py-1.5 text-[12px] text-white/90"
                        >
                            {PLATFORMS.map((p) => <option key={p.v} value={p.v}>{p.label}</option>)}
                        </select>
                    </Field>
                    <Field label="Channel">
                        <select
                            value={form.channel}
                            onChange={(e) => setForm({ ...form, channel: e.target.value })}
                            data-testid="sa-desktop-release-channel"
                            className="w-full rounded border border-white/10 bg-white/[0.03] px-2.5 py-1.5 text-[12px] text-white/90"
                        >
                            {CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
                        </select>
                    </Field>
                    <Field label="Installer file">
                        <input
                            required
                            type="file"
                            onChange={(e) => setFile(e.target.files?.[0] || null)}
                            data-testid="sa-desktop-release-file"
                            className="w-full text-[11px] text-white/70"
                        />
                    </Field>
                    <Field label=" ">
                        <button
                            type="submit"
                            disabled={uploading}
                            data-testid="sa-desktop-release-submit"
                            className="inline-flex w-full items-center justify-center gap-1.5 rounded bg-blue-500 px-3 py-1.5 text-[12px] font-medium text-white hover:bg-blue-600 disabled:opacity-50"
                        >
                            {uploading ? <Loader2 size={12} className="animate-spin" /> : <UploadCloud size={12} />}
                            {uploading ? "Uploading…" : "Upload"}
                        </button>
                    </Field>
                    <div className="sm:col-span-2 lg:col-span-5">
                        <Field label="Release notes">
                            <input
                                value={form.notes}
                                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                placeholder="What changed in this release"
                                data-testid="sa-desktop-release-notes"
                                className="w-full rounded border border-white/10 bg-white/[0.03] px-2.5 py-1.5 text-[12px] text-white/90"
                            />
                        </Field>
                    </div>
                </form>
            </SACard>

            {err && <SACard className="text-red-300">{err}</SACard>}

            <SACard className="p-0" data-testid="sa-desktop-releases-table">
                <table className="w-full text-[12px]">
                    <thead>
                        <tr className="border-b border-white/5 text-left text-[10px] uppercase tracking-wider text-white/45">
                            <th className="px-5 py-2.5 font-medium">Version</th>
                            <th className="px-2 py-2.5 font-medium">Channel</th>
                            <th className="px-2 py-2.5 font-medium">Last platform uploaded</th>
                            <th className="px-2 py-2.5 font-medium">Checksum</th>
                            <th className="px-2 py-2.5 font-medium">Released</th>
                            <th className="px-2 py-2.5 font-medium">Status</th>
                            <th className="px-5 py-2.5 text-right font-medium">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading && (
                            <tr><td colSpan={7} className="px-5 py-10 text-center text-white/40">
                                <Loader2 size={14} className="mr-2 inline animate-spin" /> Loading…
                            </td></tr>
                        )}
                        {!loading && releases.length === 0 && (
                            <tr><td colSpan={7} className="px-5 py-10 text-center text-white/40">
                                <Package size={18} className="mx-auto mb-2 opacity-40" /> No Desktop releases published yet.
                            </td></tr>
                        )}
                        {releases.map((r) => (
                            <tr key={r.id} className="border-b border-white/5" data-testid={`sa-desktop-release-row-${r.id}`}>
                                <td className="px-5 py-2.5 font-mono">{r.version}</td>
                                <td className="px-2 py-2.5 uppercase text-white/70">{r.channel}</td>
                                <td className="px-2 py-2.5 uppercase text-white/70">{r.last_uploaded_platform || "—"}</td>
                                <td className="px-2 py-2.5 font-mono text-[10.5px] text-white/50" title={r.checksum}>
                                    {r.checksum ? `${r.checksum.slice(0, 12)}…` : "—"}
                                </td>
                                <td className="px-2 py-2.5 text-white/55">{(r.released_at || "").slice(0, 10)}</td>
                                <td className="px-2 py-2.5">
                                    <span className={`rounded-full px-2 py-0.5 text-[10.5px] font-semibold ${r.published ? "bg-emerald-500/15 text-emerald-300" : "bg-white/10 text-white/50"}`}>
                                        {r.published ? "published" : "unpublished"}
                                    </span>
                                </td>
                                <td className="px-5 py-2.5 text-right">
                                    <button
                                        onClick={() => togglePublished(r)}
                                        disabled={busyId === r.id}
                                        data-testid={`sa-desktop-release-toggle-${r.id}`}
                                        className="inline-flex items-center gap-1 rounded border border-white/10 px-2 py-1 text-[11px] text-white/70 hover:bg-white/5 disabled:opacity-50"
                                    >
                                        {r.published ? <EyeOff size={11} /> : <Eye size={11} />}
                                        {r.published ? "Unpublish" : "Publish"}
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </SACard>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <div>
            <label className="mb-1 block text-[10px] uppercase tracking-wider text-white/45">{label}</label>
            {children}
        </div>
    );
}
